import type { CompatibilityProfile } from '../api/client'
import { formatLabel } from '../lib/partDisplay'

type CatalogCompatibilityFieldsProps = {
  partType: string
  compatibility: CompatibilityProfile
  overriddenPaths: string[]
  onChange: (compatibility: CompatibilityProfile) => void
}

function listToCsv(values: string[]): string {
  return values.join(', ')
}

function csvToList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function isOverridden(paths: string[], path: string): boolean {
  return paths.includes(path)
}

const FIELD_VISIBILITY: Record<string, string[]> = {
  case: ['case_diameter', 'lug_width', 'water_resistance', 'case_models', 'crown_included', 'compatible_movement_ids'],
  dial: ['dial_diameter'],
  hands: ['hand_hole_sizes'],
  movement: ['movement_id', 'compatible_movement_ids'],
  crown: ['stem_type', 'crown_diameter', 'crown_thickness', 'case_models'],
  strap: ['lug_width'],
  bezel: ['case_diameter', 'case_models'],
  crystal: ['case_diameter', 'case_models'],
}

function visibleFields(partType: string): Set<string> {
  return new Set(FIELD_VISIBILITY[partType] ?? ['case_diameter', 'lug_width', 'dial_diameter'])
}

export function CatalogCompatibilityFields({
  partType,
  compatibility,
  overriddenPaths,
  onChange,
}: CatalogCompatibilityFieldsProps) {
  const fields = visibleFields(partType)
  const handSizes = compatibility.hand_hole_sizes ?? {}

  const update = (patch: Partial<CompatibilityProfile>) => {
    onChange({ ...compatibility, ...patch })
  }

  const updateHandHole = (hand: string, value: string) => {
    const next = { ...handSizes }
    if (value) {
      next[hand] = parseFloat(value)
    } else {
      delete next[hand]
    }
    update({ hand_hole_sizes: Object.keys(next).length > 0 ? next : null })
  }

  return (
    <div className="catalog-form-grid">
      {fields.has('case_diameter') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.case_diameter') ? 'field-overridden' : ''}>
          Case diameter (mm)
          <input
            type="number"
            step="0.1"
            value={compatibility.case_diameter ?? ''}
            onChange={(event) =>
              update({
                case_diameter: event.target.value ? parseFloat(event.target.value) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('lug_width') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.lug_width') ? 'field-overridden' : ''}>
          Lug width (mm)
          <input
            type="number"
            step="0.1"
            value={compatibility.lug_width ?? ''}
            onChange={(event) =>
              update({
                lug_width: event.target.value ? parseFloat(event.target.value) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('dial_diameter') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.dial_diameter') ? 'field-overridden' : ''}>
          Dial diameter (mm)
          <input
            type="number"
            step="0.1"
            value={compatibility.dial_diameter ?? ''}
            onChange={(event) =>
              update({
                dial_diameter: event.target.value ? parseFloat(event.target.value) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('water_resistance') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.water_resistance') ? 'field-overridden' : ''}>
          Water resistance (m)
          <input
            type="number"
            value={compatibility.water_resistance ?? ''}
            onChange={(event) =>
              update({
                water_resistance: event.target.value ? parseInt(event.target.value, 10) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('movement_id') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.movement_id') ? 'field-overridden' : ''}>
          Movement ID
          <input
            value={compatibility.movement_id ?? ''}
            onChange={(event) => update({ movement_id: event.target.value || null })}
          />
        </label>
      ) : null}

      {fields.has('compatible_movement_ids') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.compatible_movement_ids') ? 'field-overridden' : ''}>
          Compatible movements (comma-separated)
          <input
            value={listToCsv(compatibility.compatible_movement_ids)}
            onChange={(event) =>
              update({ compatible_movement_ids: csvToList(event.target.value) })
            }
          />
        </label>
      ) : null}

      {fields.has('case_models') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.case_models') ? 'field-overridden' : ''}>
          Case models (comma-separated)
          <input
            value={listToCsv(compatibility.case_models)}
            onChange={(event) => update({ case_models: csvToList(event.target.value) })}
          />
        </label>
      ) : null}

      {fields.has('stem_type') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.stem_type') ? 'field-overridden' : ''}>
          Stem type
          <input
            value={compatibility.stem_type ?? ''}
            onChange={(event) => update({ stem_type: event.target.value || null })}
          />
        </label>
      ) : null}

      {fields.has('crown_diameter') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.crown_diameter') ? 'field-overridden' : ''}>
          Crown diameter (mm)
          <input
            type="number"
            step="0.1"
            value={compatibility.crown_diameter ?? ''}
            onChange={(event) =>
              update({
                crown_diameter: event.target.value ? parseFloat(event.target.value) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('crown_thickness') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.crown_thickness') ? 'field-overridden' : ''}>
          Crown thickness (mm)
          <input
            type="number"
            step="0.1"
            value={compatibility.crown_thickness ?? ''}
            onChange={(event) =>
              update({
                crown_thickness: event.target.value ? parseFloat(event.target.value) : null,
              })
            }
          />
        </label>
      ) : null}

      {fields.has('crown_included') ? (
        <label className={isOverridden(overriddenPaths, 'compatibility.crown_included') ? 'field-overridden' : ''}>
          <span className="catalog-checkbox">
            <input
              type="checkbox"
              checked={compatibility.crown_included ?? false}
              onChange={(event) => update({ crown_included: event.target.checked })}
            />
            Crown included with case
          </span>
        </label>
      ) : null}

      {fields.has('hand_hole_sizes') ? (
        <div className="catalog-hand-holes-grid">
          <label className={isOverridden(overriddenPaths, 'compatibility.hand_hole_sizes') ? 'field-overridden' : ''}>
            Hour hand hole (mm)
            <input
              type="number"
              step="0.01"
              value={handSizes.hour ?? ''}
              onChange={(event) => updateHandHole('hour', event.target.value)}
            />
          </label>
          <label>
            Minute hand hole (mm)
            <input
              type="number"
              step="0.01"
              value={handSizes.minute ?? ''}
              onChange={(event) => updateHandHole('minute', event.target.value)}
            />
          </label>
          <label>
            Second hand hole (mm)
            <input
              type="number"
              step="0.01"
              value={handSizes.second ?? ''}
              onChange={(event) => updateHandHole('second', event.target.value)}
            />
          </label>
        </div>
      ) : null}
    </div>
  )
}

export function compatibilityFieldsSummary(partType: string): string {
  const fields = FIELD_VISIBILITY[partType]
  if (!fields) return 'General dimensions'
  return fields.map((f) => formatLabel(f.replace(/_/g, ' '))).join(', ')
}
